import type { MasterFieldDef } from '../constants/masters';
import {
  FOOD_IMAGE_MASTER_MAX_HEIGHT,
  FOOD_IMAGE_MASTER_MAX_WIDTH,
} from '../constants/foodMasterRules';
import type { ImageConstraints } from './imageValidation';

const FOOD_MASTER_IMAGE_TYPES = new Set([
  'restaurant_types',
  'food_categories',
  'food_sub_categories',
  'cuisine_types',
  'addon_groups',
  'dietary_types',
  'food_images',
]);

/** Food masters without enforced upload dimension limits. */
const FOOD_MASTER_IMAGE_NO_CONSTRAINT_TYPES = new Set([
  'restaurant_types',
  'food_categories',
  'food_sub_categories',
  'cuisine_types',
  'addon_groups',
]);

const DIETARY_ICON_CONSTRAINTS: ImageConstraints = {
  maxWidth: 256,
  maxHeight: 256,
  aspectRatio: '1:1',
};

const FOOD_IMAGE_MASTER_CONSTRAINTS: ImageConstraints = {
  maxWidth: FOOD_IMAGE_MASTER_MAX_WIDTH,
  maxHeight: FOOD_IMAGE_MASTER_MAX_HEIGHT,
};

function isImageMasterField(field: Pick<MasterFieldDef, 'key' | 'label' | 'type'>): boolean {
  return field.type === 'image'
    || field.type === 'url'
    || field.key === 'imageUrl'
    || field.key === 'icon';
}

export function resolveMasterImageConstraints(
  field: MasterFieldDef,
  masterTypeKey: string,
  _metadata: Record<string, string | number | boolean> = {},
): ImageConstraints | undefined {
  if (!isImageMasterField(field)) return undefined;

  if (masterTypeKey === 'dietary_types' && field.key === 'icon') {
    return DIETARY_ICON_CONSTRAINTS;
  }

  if (masterTypeKey === 'food_images') {
    return {
      maxWidth: field.imageMaxWidth ?? FOOD_IMAGE_MASTER_CONSTRAINTS.maxWidth,
      maxHeight: field.imageMaxHeight ?? FOOD_IMAGE_MASTER_CONSTRAINTS.maxHeight,
    };
  }

  if (masterTypeKey === 'grocery_product_images') {
    const typeConstraints = (() => {
      const imageType = String(_metadata.imageType ?? 'thumbnail').trim().toLowerCase();
      if (imageType === 'gallery') {
        return { maxWidth: 1200, maxHeight: 800 };
      }
      return { maxWidth: 800, maxHeight: 800, aspectRatio: '1:1' as const };
    })();
    return {
      maxWidth: field.imageMaxWidth ?? typeConstraints.maxWidth,
      maxHeight: field.imageMaxHeight ?? typeConstraints.maxHeight,
      aspectRatio: field.imageAspectRatio ?? typeConstraints.aspectRatio,
    };
  }

  if (FOOD_MASTER_IMAGE_NO_CONSTRAINT_TYPES.has(masterTypeKey)) return undefined;

  const merged: ImageConstraints = {
    minWidth: field.imageMinWidth,
    minHeight: field.imageMinHeight,
    maxWidth: field.imageMaxWidth,
    maxHeight: field.imageMaxHeight,
    aspectRatio: field.imageAspectRatio,
  };

  const hasLimits = merged.minWidth || merged.minHeight || merged.maxWidth || merged.maxHeight || merged.aspectRatio;
  return hasLimits ? merged : undefined;
}

export function formatImageConstraintHint(constraints?: ImageConstraints): string | null {
  if (!constraints) return null;
  const parts: string[] = [];
  if (constraints.minWidth && constraints.minHeight) {
    parts.push(`${constraints.minWidth}×${constraints.minHeight}px min`);
  }
  if (constraints.maxWidth && constraints.maxHeight) {
    parts.push(`${constraints.maxWidth}×${constraints.maxHeight}px max`);
  }
  if (constraints.aspectRatio) {
    parts.push(`ratio ${constraints.aspectRatio}`);
  }
  return parts.length ? parts.join(' · ') : null;
}

export { FOOD_MASTER_IMAGE_TYPES, DIETARY_ICON_CONSTRAINTS, FOOD_IMAGE_MASTER_CONSTRAINTS };
